/**
 * Performance testing contracts (docs/design/performance-testing.md): the PerfScript IR and run settings stored on
 * PERFORMANCE tests, load limits, and the 1QA Load Agent protocol. Engine-neutral: nothing here is k6-specific
 * (the k6 adapter lives in @1qa/perf and runs inside the Load Agent).
 */
import { z } from 'zod';
import type { RunScenarioData } from './perfData.js';
import type { RunFile } from './perfFiles.js';
import type { OrgRole } from './roles.js';
import { RUN_QUEUE_NAME } from './testing.js';

// ---------- PerfScript IR ----------

const varName = z.string().regex(/^[A-Za-z_][A-Za-z0-9_]{0,63}$/, 'letters, digits and _ only');
const header = z.object({ name: z.string().min(1).max(200), value: z.string().max(8000) });

export const extractorSchema = z.object({
  var: varName,
  from: z.enum(['json', 'header', 'html', 'regex', 'cookie']),
  /** json: `$.a.b[0]` / `$.items[?(@.sku=='B2')].id`; header / cookie: name; html: `input[name="csrf"]@value`; regex: one capture group. */
  expr: z.string().min(1).max(2000),
  regex: z.string().max(2000).optional(),
  decode: z.enum(['url', 'html']).optional(),
  pick: z.enum(['first', 'random', 'all']),
  /** How the correlation was found (confidence shown in the review screen). */
  origin: z.enum(['search', 'diff', 'rule', 'ai', 'user']),
  required: z.boolean(),
});

export const checkSchema = z.object({ kind: z.enum(['status', 'text', 'notText']), expect: z.union([z.string().max(2000), z.number().int()]) });

/**
 * A file the request sends: the name of one of the performance project's test files ("cat.png"), or a template that
 * names one per journey ("{{IMAGE}}", a data file column). Empty: not chosen yet (a recorded upload; the browser does
 * not record the file itself). With `source: 'agent'` it is a path inside the load agent's files folder instead
 * ("photos/{{IMAGE}}"), see perfFiles.ts.
 */
const fileRef = z.string().max(500);
/** Where a file comes from: the project's test files in 1QA (default), or the load agent's files folder. */
const fileSource = z.enum(['project', 'agent']).optional();

/** One part of a multipart/form-data body: a text field, or a file field (perfFiles.ts). */
export const multipartPartSchema = z.union([
  z.object({ name: z.string().min(1).max(200), value: z.string().max(1_000_000) }),
  z.object({
    name: z.string().min(1).max(200),
    file: fileRef,
    source: fileSource,
    /** File name sent in the part (default: the test file's name). */
    filename: z.string().max(500).optional(),
    /** Default: the test file's type. */
    contentType: z.string().max(200).optional(),
  }),
]);

export const bodySchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('none') }),
  z.object({ kind: z.enum(['json', 'form', 'text']), value: z.string().max(1_000_000), contentType: z.string().max(200).optional() }),
  /** An upload form: text and file fields (the engine writes the boundary and Content-Type). */
  z.object({ kind: z.literal('multipart'), parts: z.array(multipartPartSchema).min(1).max(50) }),
  /** The body is one test file as it is (e.g. PUT to a storage URL); Content-Type defaults to the file's type. */
  z.object({ kind: z.literal('file'), file: fileRef, source: fileSource, contentType: z.string().max(200).optional() }),
]);

export const httpStepSchema = z.object({
  kind: z.literal('http'),
  id: z.string().min(1).max(40),
  /** Low-cardinality metrics name, e.g. `POST /cart/{cartId}/items`. */
  name: z.string().min(1).max(200),
  method: z.string().regex(/^[A-Z]{3,10}$/),
  /** Absolute, or a path relative to the script's base URL; may contain {{templates}}. */
  url: z.string().min(1).max(8000),
  headers: z.array(header).max(100),
  body: bodySchema,
  extract: z.array(extractorSchema).max(50),
  checks: z.array(checkSchema).max(20),
  /** Kept in the script but not sent (the editor's "Skip"); see runnableScript. */
  skip: z.boolean().optional(),
});

export const wsActionSchema = z.discriminatedUnion('op', [
  z.object({ op: z.literal('send'), message: z.string().max(1_000_000) }),
  z.object({ op: z.literal('expect'), match: z.string().max(2000), timeoutMs: z.number().int().min(100).max(300_000), extract: z.array(extractorSchema).max(50) }),
  z.object({ op: z.literal('close') }),
]);

export const wsStepSchema = z.object({
  kind: z.literal('ws'),
  id: z.string().min(1).max(40),
  name: z.string().min(1).max(200),
  url: z.string().min(1).max(8000),
  headers: z.array(header).max(100),
  actions: z.array(wsActionSchema).max(500),
  skip: z.boolean().optional(),
});

export const perfStepSchema = z.discriminatedUnion('kind', [httpStepSchema, wsStepSchema]);

export const transactionSchema = z.object({
  id: z.string().regex(/^[A-Za-z0-9_-]{1,40}$/),
  name: z.string().trim().min(1).max(120),
  steps: z.array(perfStepSchema).min(1).max(200),
  thinkTimeMs: z.object({ min: z.number().int().min(0).max(600_000), max: z.number().int().min(0).max(600_000) }).refine((t) => t.max >= t.min, 'max below min').optional(),
});

export const generatorSchema = z.discriminatedUnion('fn', [
  z.object({ fn: z.enum(['uuid', 'timestamp_ms', 'timestamp_s', 'iso_now', 'pkce_verifier']) }),
  z.object({ fn: z.enum(['random_hex', 'random_alnum']), length: z.number().int().min(1).max(256) }),
  z.object({ fn: z.literal('random_int'), min: z.number().int(), max: z.number().int() }),
  z.object({ fn: z.literal('pkce_challenge'), of: varName }),
]);

export const perfScriptSchema = z
  .object({
    version: z.literal(1),
    baseUrl: z.string().min(1).max(2000),
    defaults: z.object({ headers: z.array(header).max(50), timeoutMs: z.number().int().min(1000).max(600_000) }),
    iterationVars: z.array(z.object({ name: varName, gen: generatorSchema })).max(50),
    /** Test data keys the script reads; values come from the run's test data / collection, never the script. */
    dataKeys: z.array(z.string().max(100)).max(200),
    /** Secret test data keys; delivered to the engine's secret source only. */
    secretKeys: z.array(z.string().max(100)).max(50),
    transactions: z.array(transactionSchema).min(1).max(100),
  })
  .refine((s) => s.transactions.reduce((n, t) => n + t.steps.length, 0) <= 1000, 'a script can have at most 1,000 steps')
  .refine((s) => new Set(s.transactions.map((t) => t.id)).size === s.transactions.length, 'transaction ids must be unique');

export type Extractor = z.infer<typeof extractorSchema>;
export type ExtractFrom = Extractor['from'];
export type ExtractOrigin = Extractor['origin'];
export type Check = z.infer<typeof checkSchema>;
export type Body = z.infer<typeof bodySchema>;
export type MultipartPart = z.infer<typeof multipartPartSchema>;
/** A file field of a multipart body. */
export type MultipartFilePart = Extract<MultipartPart, { file: string }>;
export const isFilePart = (p: MultipartPart): p is MultipartFilePart => 'file' in p;
export type Header = z.infer<typeof header>;
export type HttpStep = z.infer<typeof httpStepSchema>;
export type WsAction = z.infer<typeof wsActionSchema>;
export type WsStep = z.infer<typeof wsStepSchema>;
export type Step = z.infer<typeof perfStepSchema>;
export type Transaction = z.infer<typeof transactionSchema>;
export type Generator = z.infer<typeof generatorSchema>;
export type PerfScript = z.infer<typeof perfScriptSchema>;

// ---------- Script variables: what {{templates}} use and where values come from (script editor, API) ----------

const TEMPLATE_RE = /\{\{\s*([^{}]+?)\s*\}\}/g;

/** Names a text's {{templates}} use, without `:url` / `:form` / `:json` and `|written text`; generators start with `$`. */
export function templateNames(text: string): string[] {
  const out: string[] = [];
  for (const m of text.matchAll(TEMPLATE_RE)) {
    const inner = m[1]!;
    const ref = (inner.includes('|') ? inner.slice(0, inner.indexOf('|')) : inner).trim();
    out.push(ref.replace(/:(url|form|json)$/, ''));
  }
  return out;
}

/** Where a text sits in a request, which decides how a value is written into it. */
export type TextPlace = 'url' | 'header' | 'json' | 'form' | 'text';

/** The texts of a request body that may hold templates: the body itself, or each field's value and file name. */
function bodyTexts(step: HttpStep): { place: TextPlace; get: () => string; set: (v: string) => Step }[] {
  const body = step.body;
  if (body.kind === 'none') return [];
  if (body.kind === 'file') return [{ place: 'text', get: () => body.file, set: (v) => ({ ...step, body: { ...body, file: v } }) }];
  if (body.kind === 'multipart') {
    const setPart = (i: number, part: MultipartPart): Step => ({ ...step, body: { ...body, parts: body.parts.map((x, k) => (k === i ? part : x)) } });
    return body.parts.map((part, i) =>
      isFilePart(part)
        ? { place: 'text' as const, get: () => part.file, set: (v: string) => setPart(i, { ...part, file: v }) }
        : { place: 'text' as const, get: () => part.value, set: (v: string) => setPart(i, { ...part, value: v }) },
    );
  }
  return [{ place: body.kind, get: () => body.value, set: (v) => ({ ...step, body: { ...body, value: v } }) }];
}

/** The texts of a step that may hold templates, each with a setter (for find-and-replace). */
function stepTexts(step: Step): { place: TextPlace; get: () => string; set: (v: string) => Step }[] {
  const headers = step.headers.map((h, i) => ({
    place: 'header' as const,
    get: () => h.value,
    set: (v: string) => ({ ...step, headers: step.headers.map((x, k) => (k === i ? { ...x, value: v } : x)) }) as Step,
  }));
  if (step.kind === 'http') {
    return [{ place: 'url', get: () => step.url, set: (v) => ({ ...step, url: v }) }, ...headers, ...bodyTexts(step)];
  }
  return [
    { place: 'url', get: () => step.url, set: (v) => ({ ...step, url: v }) },
    ...headers,
    ...step.actions.flatMap((a, i) =>
      a.op === 'send'
        ? [{ place: 'text' as const, get: () => a.message, set: (v: string) => ({ ...step, actions: step.actions.map((x, k) => (k === i ? { ...a, message: v } : x)) }) as Step }]
        : [],
    ),
  ];
}

const stepUses = (step: Step) => stepTexts(step).flatMap((t) => templateNames(t.get()));
const stepTakes = (step: Step): Extractor[] => (step.kind === 'http' ? step.extract : step.actions.flatMap((a) => (a.op === 'expect' ? a.extract : [])));

export interface ScriptVariables {
  /** Values taken from responses (correlation): name -> the first step taking it. */
  extracted: Map<string, string>;
  /** Names each step uses. */
  uses: Map<string, string[]>;
  /** Test data the script reads: used names that are not taken from a response, iteration values or generators. */
  data: string[];
  /** Values used before (or in) the step that takes them: the request would send the {{template}} as written. */
  usedTooEarly: { stepId: string; name: string }[];
}

/** What a script's templates use and where each value comes from, in request order. */
export function scriptVariables(script: PerfScript): ScriptVariables {
  const extracted = new Map<string, string>();
  const uses = new Map<string, string[]>();
  const iteration = new Set(script.iterationVars.map((v) => v.name));
  const all = script.transactions.flatMap((t) => t.steps);
  // Skipped steps take nothing and need no test data; their uses are still listed for the editor.
  for (const st of all) if (st.skip) uses.set(st.id, stepUses(st));
  const steps = all.filter((st) => !st.skip);
  for (const st of steps) for (const e of stepTakes(st)) if (!extracted.has(e.var)) extracted.set(e.var, st.id);
  const seen = new Set<string>();
  const usedTooEarly: ScriptVariables['usedTooEarly'] = [];
  const data = new Set<string>();
  const note = (name: string) => {
    if (!name.startsWith('$') && !iteration.has(name) && !extracted.has(name)) data.add(name);
  };
  for (const n of [script.baseUrl, ...script.defaults.headers.map((h) => h.value)].flatMap(templateNames)) note(n);
  for (const st of steps) {
    const names = stepUses(st);
    uses.set(st.id, names);
    for (const n of names) {
      note(n);
      if (extracted.has(n) && !seen.has(n)) usedTooEarly.push({ stepId: st.id, name: n });
    }
    for (const e of stepTakes(st)) seen.add(e.var);
  }
  return { extracted, uses, data: [...data], usedTooEarly };
}

/**
 * The script as it runs: skipped steps left out, and transactions with nothing left (runs, checks, profiles and
 * compilation read this; the saved script keeps the skipped steps so they can be turned back on).
 */
export function runnableScript(script: PerfScript): PerfScript {
  if (!script.transactions.some((t) => t.steps.some((st) => st.skip))) return script;
  return { ...script, transactions: script.transactions.map((t) => ({ ...t, steps: t.steps.filter((st) => !st.skip) })).filter((t) => t.steps.length > 0) };
}

/**
 * Requests of a saved script the browser's front end made for itself (a page with `Accept: text/html`, Next.js React
 * Server Component requests with `_rsc` / `RSC: 1` / `Next-Router-*`, prefetches): the script editor's "Remove front-end
 * requests". From the request alone (scripts keep no responses), so a page saved without its headers is not found.
 */
export function frontEndSteps(script: PerfScript): string[] {
  return script.transactions.flatMap((t) =>
    t.steps.flatMap((st) => {
      if (st.kind !== 'http' || (st.method !== 'GET' && st.method !== 'HEAD')) return [];
      const h = (n: string) => st.headers.find((x) => x.name.toLowerCase() === n)?.value ?? '';
      const front =
        /[?&]_rsc=/.test(st.url) ||
        h('rsc') === '1' ||
        st.headers.some((x) => /^next-router-/i.test(x.name)) ||
        /prefetch/i.test(h('purpose') + h('sec-purpose')) ||
        /^text\/html/i.test(h('accept')) ||
        Boolean(h('sec-fetch-dest') && h('sec-fetch-dest') !== 'empty');
      return front ? [st.id] : [];
    }),
  );
}

/** How `value` appears in a text at `place` (recordings keep values encoded the way the browser sent them). */
function encodedForms(value: string, place: TextPlace): { text: string; enc: '' | ':url' | ':form' | ':json' }[] {
  const forms: { text: string; enc: '' | ':url' | ':form' | ':json' }[] = [];
  // Always encoded for the place (a later value may hold characters the recorded one did not: a quote, a + or /).
  const add = (text: string, enc: '' | ':url' | ':form' | ':json') => {
    if (text && !forms.some((f) => f.text === text)) forms.push({ text, enc });
  };
  if (place === 'url') add(encodeURIComponent(value), ':url');
  if (place === 'form') add(encodeURIComponent(value).replace(/%20/g, '+'), ':form');
  if (place === 'json') add(JSON.stringify(value).slice(1, -1), ':json');
  add(value, '');
  return forms;
}

/** Replaces `find` in the literal parts of a text only, never inside existing {{templates}}. */
function replaceLiteral(text: string, find: string, repl: string): { text: string; count: number } {
  let count = 0;
  const out = text
    .split(/(\{\{[^{}]*\}\})/)
    .map((part, i) => (i % 2 === 1 ? part : part.split(find).reduce((acc, piece, k) => (k === 0 ? piece : (count++, acc + repl + piece)), '')))
    .join('');
  return { text: out, count };
}

/**
 * Writes `{{name}}` wherever `value` appears in the requests (encoded the way each place holds it, with `:url` / `:form`
 * / `:json` so the value is encoded again at run time), from the step after `afterStepId` on (correlation: only later
 * requests use a value taken from a response) or everywhere (test data). Returns the script and how many were replaced.
 */
export function replaceValue(script: PerfScript, value: string, name: string, afterStepId?: string): { script: PerfScript; count: number } {
  let count = 0;
  let on = afterStepId === undefined;
  const transactions = script.transactions.map((t) => ({
    ...t,
    steps: t.steps.map((st) => {
      if (!on) {
        if (st.id === afterStepId) on = true;
        return st;
      }
      let step = st;
      for (let i = 0; i < stepTexts(step).length; i++) {
        const field = stepTexts(step)[i]!;
        let text = field.get();
        for (const f of encodedForms(value, field.place)) {
          const r = replaceLiteral(text, f.text, `{{${name}${f.enc}}}`);
          text = r.text;
          count += r.count;
        }
        if (text !== field.get()) step = field.set(text);
      }
      return step;
    }),
  }));
  return { script: { ...script, transactions }, count };
}

// ---------- Workload and SLOs (stored on the test as perfConfig, overridable per run) ----------

const stageS = z.number().int().min(1).max(7 * 86_400);

export const workloadModelSchema = z.discriminatedUnion('kind', [
  /** Fixed users x fixed iterations: verify (1 x 1) and smoke checks. */
  z.object({ kind: z.literal('iterations'), vus: z.number().int().min(1).max(100_000), iterations: z.number().int().min(1).max(1_000_000) }),
  /** Closed model: concurrent users. */
  z.object({ kind: z.literal('closed'), stages: z.array(z.object({ durationS: stageS, vus: z.number().int().min(0).max(100_000) })).min(1).max(50) }),
  /** Open model: arrivals (iteration starts) per time unit, independent of response times. */
  z.object({
    kind: z.literal('open'),
    stages: z.array(z.object({ durationS: stageS, rate: z.number().int().min(0).max(1_000_000) })).min(1).max(50),
    /** `rate` per second (default) or per minute. */
    timeUnit: z.enum(['s', 'm']).optional(),
    preAllocatedVus: z.number().int().min(1).max(100_000),
    maxVus: z.number().int().min(1).max(100_000),
  }),
]);

export const sloSchema = z.object({
  metric: z.enum(['p50', 'p90', 'p95', 'p99', 'errorRate']),
  /** 'all' or a transaction name. */
  scope: z.string().min(1).max(120),
  /** ms for percentiles; a fraction (0.01 = 1 %) for errorRate. */
  max: z.number().positive().max(3_600_000),
});

export const PERF_TEMPLATES = ['smoke', 'load', 'stress', 'spike', 'soak'] as const;
export type PerfTemplate = (typeof PERF_TEMPLATES)[number];

// ---------- Performance tests: load plans over scripts ----------
// A performance test is its own thing, not a functional test: one or more scenarios (each a script from the project's
// script library with its share of the load, start delay, think time / pacing) under one load strategy (users or
// arrivals, ramp-up / steady / ramp-down or own stages) with goals. docs/design/performance-testing.md "Load plans".

const planSeconds = z.number().int().min(0).max(7 * 86_400);

/** Users mode: how fast users start (or stop), e.g. 5 per second; the ramp time follows from the total users. */
export const rampRateSchema = z.object({ users: z.number().int().min(1).max(100_000), per: z.enum(['s', 'm']) });
export type RampRate = z.infer<typeof rampRateSchema>;

/** Seconds to start (or stop) `total` users at `rate` (at least 1 s): 100 users at 5 per second = 20 s. */
export const rampSeconds = (total: number, rate: RampRate): number => Math.max(1, Math.round((total / rate.users) * (rate.per === 'm' ? 60 : 1)));

/** A ramp given in seconds as a rate, to show it: whole users per second, or per minute when slower. Null: all at once. */
export function rampRateOf(total: number, seconds: number): RampRate | null {
  if (seconds <= 0) return null;
  const perS = total / seconds;
  return perS >= 1 ? { users: Math.round(perS), per: 's' } : { users: Math.max(1, Math.round(perS * 60)), per: 'm' };
}

/** How the load builds up, holds and stops, for the whole test (scenarios take their share of it). */
export const loadShapeSchema = z
  .object({
    /** users: concurrent users (closed model); rate: new iterations per time unit (open model, "users per minute"). */
    mode: z.enum(['users', 'rate']),
    /** Peak users, or peak arrivals per `timeUnit`. */
    target: z.number().int().min(1).max(1_000_000),
    timeUnit: z.enum(['s', 'm']).default('s'),
    rampUpS: planSeconds,
    steadyS: planSeconds,
    rampDownS: planSeconds,
    /** Own shape instead of ramp-up / steady / ramp-down (stress staircase, spike): each stage ramps to its target. */
    stages: z.array(z.object({ durationS: stageS, target: z.number().int().min(0).max(1_000_000) })).min(1).max(50).optional(),
    /** rate mode: most users the agents may start to keep up (default: estimated from the scripts). */
    maxVus: z.number().int().min(1).max(100_000).optional(),
    /**
     * Users mode: start `target` users at this rate instead of over `rampUpS` (stop them at `rampDownRate`). The ramp
     * times are worked out from them on parse (withRampRates), so the rest of 1QA only reads rampUpS / rampDownS.
     */
    rampUpRate: rampRateSchema.optional(),
    rampDownRate: rampRateSchema.optional(),
  })
  .transform((l) => withRampRates(l))
  .refine((l) => (l.stages ? l.stages.length > 0 : l.rampUpS + l.steadyS + l.rampDownS > 0), 'The test needs a duration');
export type LoadShape = z.infer<typeof loadShapeSchema>;

/** Ramp-up / ramp-down seconds from the ramp rates (users mode with ramp-up / steady / ramp-down; otherwise unchanged). */
export function withRampRates<T extends { mode: 'users' | 'rate'; target: number; rampUpS: number; rampDownS: number; stages?: unknown; rampUpRate?: RampRate; rampDownRate?: RampRate }>(load: T): T {
  if (load.mode !== 'users' || load.stages) return load;
  return {
    ...load,
    ...(load.rampUpRate ? { rampUpS: rampSeconds(load.target, load.rampUpRate) } : {}),
    ...(load.rampDownRate ? { rampDownS: rampSeconds(load.target, load.rampDownRate) } : {}),
  };
}

/** Pause between transactions: as recorded in the script, none, or a random time between min and max seconds. */
export const thinkTimeSchema = z.union([
  z.literal('recorded'),
  z.literal('none'),
  z.object({ minS: z.number().min(0).max(600), maxS: z.number().min(0).max(600) }).refine((t) => t.maxS >= t.minS, 'max below min'),
]);
export type ThinkTime = z.infer<typeof thinkTimeSchema>;

export const scenarioSchema = z.object({
  /** Stable id, also the k6 scenario name: lower-case letters and digits. */
  id: z.string().regex(/^[a-z0-9]{1,12}$/),
  name: z.string().trim().min(1).max(80),
  /** A script of the project's library (LoadScript). */
  scriptId: z.string().min(1).max(40),
  /** Share of the load in percent; the shares of a test add up to 100 (with `split: 'users'` they follow from `users`). */
  share: z.number().min(0.1).max(100),
  /** `split: 'users'`: this scenario's own number of users; the test's maximum users is their sum. */
  users: z.number().int().min(1).max(100_000).optional(),
  /** Starts this long after the test (its ramp-up starts then). */
  startAfterS: planSeconds.default(0),
  thinkTime: thinkTimeSchema.default('recorded'),
  /** users mode: each user starts an iteration at most every `pacingS` seconds (waits out the rest). */
  pacingS: z.number().min(0).max(3600).optional(),
  /** A data file of the project (PerfDataSet): each journey takes its next row (perfData.ts). */
  dataSetId: z.string().min(1).max(40).optional(),
  /** When every row has been used: start again at row 1 (default) or stop sending. */
  whenUsedUp: z.enum(['loop', 'stop']).optional(),
});
export type Scenario = z.infer<typeof scenarioSchema>;

/** What a scenario does when its data file's rows are all used: start again at row 1, or its users stop sending. */
export const DATA_WHEN_USED_UP = ['loop', 'stop'] as const;
export type DataWhenUsedUp = (typeof DATA_WHEN_USED_UP)[number];

/** TestCase.perfConfig of a PERFORMANCE test. */
export const perfConfigSchema = z
  .object({
    version: z.literal(2),
    template: z.enum(PERF_TEMPLATES),
    load: loadShapeSchema,
    /**
     * How the maximum users are split between the scenarios: equally, by percentage (`share`), or by a number of users
     * per scenario (`users`; users mode with ramp-up / steady / ramp-down only). Always kept in `share` as well.
     */
    split: z.enum(['even', 'percent', 'users']).default('percent'),
    scenarios: z.array(scenarioSchema).min(1).max(20),
    slos: z.array(sloSchema).max(50),
  })
  .superRefine((c, ctx) => {
    if (c.split === 'users') {
      if (c.load.mode !== 'users' || c.load.stages) ctx.addIssue({ code: 'custom', path: ['split'], message: 'Users per scenario need “Users at the same time” with ramp-up, steady and ramp-down' });
      else if (c.scenarios.some((sc) => !sc.users)) ctx.addIssue({ code: 'custom', path: ['scenarios'], message: 'Every scenario needs its number of users' });
      else {
        const sum = c.scenarios.reduce((n, sc) => n + sc.users!, 0);
        if (sum !== c.load.target) ctx.addIssue({ code: 'custom', path: ['load', 'target'], message: `The scenarios' users add up to ${sum}, not the test's ${c.load.target}` });
      }
    }
    const total = c.scenarios.reduce((n, sc) => n + sc.share, 0);
    if (Math.abs(total - 100) > 0.5) ctx.addIssue({ code: 'custom', path: ['scenarios'], message: `The scenarios' shares add up to ${+total.toFixed(1)} %, not 100 %` });
    if (new Set(c.scenarios.map((sc) => sc.id)).size !== c.scenarios.length) ctx.addIssue({ code: 'custom', path: ['scenarios'], message: 'Two scenarios have the same id' });
    if (new Set(c.scenarios.map((sc) => sc.name.toLowerCase())).size !== c.scenarios.length) ctx.addIssue({ code: 'custom', path: ['scenarios'], message: 'Two scenarios have the same name' });
  });

export type WorkloadModel = z.infer<typeof workloadModelSchema>;
export type Slo = z.infer<typeof sloSchema>;
export type PerfConfig = z.infer<typeof perfConfigSchema>;

/** Stages of the shape at full size (ramp-up, steady, ramp-down, without empty ones). */
export function shapeStages(load: LoadShape): { durationS: number; target: number }[] {
  if (load.stages) return load.stages;
  return [
    { durationS: load.rampUpS, target: load.target },
    { durationS: load.steadyS, target: load.target },
    { durationS: load.rampDownS, target: 0 },
  ].filter((st) => st.durationS > 0);
}

/** A scenario's own workload: its share of the shape, from its start delay on. */
export interface ScenarioWorkload {
  scenarioId: string;
  startAfterS: number;
  model: WorkloadModel;
}

/** Most users an arrival rate needs: arrivals per second x the seconds an iteration takes, with headroom. */
export function vusForRate(perSecond: number, iterationSeconds: number): number {
  return Math.max(2, Math.ceil(perSecond * Math.max(1, iterationSeconds) * 1.5));
}

/**
 * `total` split by `weights` into whole numbers that add up to `total` (largest remainder): 100 users at 34 / 33 / 33 %
 * -> 34, 33, 33; 10 users -> 4, 3, 3. While there are enough, every weight gets at least 1 (taken from the largest).
 */
export function shareOut(total: number, weights: number[]): number[] {
  const sum = weights.reduce((n, w) => n + w, 0) || 1;
  const exact = weights.map((w) => (total * w) / sum);
  const out = exact.map(Math.floor);
  const order = exact.map((x, i) => [x - Math.floor(x), i] as const).sort((a, b) => b[0] - a[0] || a[1] - b[1]);
  for (let left = total - out.reduce((n, x) => n + x, 0), k = 0; left > 0; left--, k++) out[order[k % order.length]![1]]! += 1;
  if (total >= weights.length) {
    for (let i = 0; i < out.length; i++) {
      if (out[i]! > 0) continue;
      const big = out.indexOf(Math.max(...out));
      out[big]! -= 1;
      out[i] = 1;
    }
  }
  return out;
}

/**
 * The load of each scenario: its share of the shape, at least 1 user / arrival while the shape is above 0. In users
 * mode the peak is split into whole users that add up to it (shareOut), so 100 users over 3 scenarios stay 100.
 * `iterationSeconds` (per scenario id, from the script profiles) sizes the users an arrival rate may start when the plan
 * does not say. Arrivals per second that would become fractions are given per minute instead.
 */
export function scenarioWorkloads(plan: Pick<PerfConfig, 'load' | 'scenarios'>, iterationSeconds: Record<string, number> = {}): ScenarioWorkload[] {
  const total = plan.scenarios.reduce((n, sc) => n + sc.share, 0) || 1;
  const stages = shapeStages(plan.load);
  const peak = Math.max(0, ...stages.map((st) => st.target));
  const peaks = shareOut(peak, plan.scenarios.map((sc) => sc.share));
  return plan.scenarios.map((sc, i) => {
    const f = sc.share / total;
    const scale = (t: number) => (t === 0 ? 0 : Math.max(1, Math.round(t * f)));
    if (plan.load.mode === 'users') {
      const users = (t: number) => (t === 0 ? 0 : t === peak ? Math.max(1, peaks[i]!) : Math.max(1, Math.round((t * peaks[i]!) / peak)));
      return { scenarioId: sc.id, startAfterS: sc.startAfterS, model: { kind: 'closed', stages: stages.map((st) => ({ durationS: st.durationS, vus: users(st.target) })) } };
    }
    const perMinute = plan.load.timeUnit === 'm' || stages.some((st) => !Number.isInteger(st.target * f));
    const toUnit = plan.load.timeUnit === 's' && perMinute ? 60 : 1;
    const rated = stages.map((st) => ({ durationS: st.durationS, rate: scale(st.target * toUnit) }));
    const peakPerS = Math.max(...rated.map((st) => st.rate)) / (perMinute ? 60 : 1);
    const maxVus = plan.load.maxVus ? Math.max(1, Math.round(plan.load.maxVus * f)) : vusForRate(peakPerS, iterationSeconds[sc.id] ?? 10);
    const model: WorkloadModel = { kind: 'open', stages: rated, timeUnit: perMinute ? 'm' : 's', preAllocatedVus: Math.max(1, Math.ceil(maxVus / 2)), maxVus };
    return { scenarioId: sc.id, startAfterS: sc.startAfterS, model };
  });
}

/**
 * The whole test as one workload: the scenarios added up, each shifted by its start delay (between stage ends the
 * load changes linearly, as k6 ramps it). Load limits, capacity estimates, the planned duration and the steady window
 * are judged on it.
 */
export function totalWorkload(parts: ScenarioWorkload[]): WorkloadModel {
  if (parts.length === 1 && parts[0]!.startAfterS === 0) return parts[0]!.model;
  const open = parts[0]!.model.kind === 'open';
  // Each part as points (seconds, users or arrivals per second) of a piecewise linear function that starts at 0.
  const curves = parts.map((p) => {
    const pts: [number, number][] = [[p.startAfterS, 0]];
    const m = p.model;
    if (m.kind === 'iterations') return pts;
    let t = p.startAfterS;
    for (const st of m.stages) {
      t += st.durationS;
      pts.push([t, 'vus' in st ? st.vus : st.rate / (m.kind === 'open' && m.timeUnit === 'm' ? 60 : 1)]);
    }
    return pts;
  });
  // Value just before x (a curve ending at x still counts with its last value).
  const before = (pts: [number, number][], x: number) => {
    if (x <= pts[0]![0] || x > pts[pts.length - 1]![0]) return 0;
    for (let i = 1; i < pts.length; i++) {
      const [x0, y0] = pts[i - 1]!;
      const [x1, y1] = pts[i]!;
      if (x <= x1) return y0 + ((y1 - y0) * (x - x0)) / (x1 - x0 || 1);
    }
    return 0;
  };
  const times = [...new Set(curves.flatMap((c) => c.map(([x]) => x)))].filter((x) => x > 0).sort((a, b) => a - b);
  const stages: { durationS: number; value: number }[] = [];
  let prev = 0;
  for (const x of times) {
    if (x - prev < 1) continue;
    stages.push({ durationS: Math.round(x - prev), value: curves.reduce((n, c) => n + before(c, x), 0) });
    prev = x;
  }
  if (!stages.length) stages.push({ durationS: 1, value: 0 });
  if (!open) return { kind: 'closed', stages: stages.map((st) => ({ durationS: st.durationS, vus: Math.round(st.value) })) };
  const sum = (k: 'maxVus' | 'preAllocatedVus') => parts.reduce((n, p) => n + (p.model.kind === 'open' ? p.model[k] : 0), 0);
  // Per minute, so small rates stay whole numbers.
  return { kind: 'open', timeUnit: 'm', stages: stages.map((st) => ({ durationS: st.durationS, rate: Math.round(st.value * 60) })), preAllocatedVus: sum('preAllocatedVus'), maxVus: sum('maxVus') };
}

/** What each template sets up, around a target (the editor applies it to the shape; everything stays editable). */
export function templateShape(template: PerfTemplate, target: number, mode: LoadShape['mode'] = 'users'): LoadShape {
  const base: LoadShape = { mode, target, timeUnit: 's', rampUpS: 0, steadyS: 0, rampDownS: 0 };
  switch (template) {
    case 'smoke':
      return { ...base, target: Math.min(target, 5), steadyS: 60 };
    case 'load':
      return { ...base, rampUpS: 180, steadyS: 20 * 60, rampDownS: 60 };
    case 'stress': {
      // A staircase up to twice the target, to find where the goals start to fail.
      const step = Math.max(1, Math.round(target / 2));
      const stairs = [1, 2, 3, 4].flatMap((k) => [{ durationS: 60, target: step * k }, { durationS: 180, target: step * k }]);
      return { ...base, target: step * 4, stages: [...stairs, { durationS: 60, target: 0 }] };
    }
    case 'spike':
      return {
        ...base,
        target: target * 8,
        stages: [{ durationS: 60, target }, { durationS: 120, target }, { durationS: 10, target: target * 8 }, { durationS: 120, target: target * 8 }, { durationS: 10, target }, { durationS: 120, target }, { durationS: 30, target: 0 }],
      };
    case 'soak':
      return { ...base, rampUpS: 300, steadyS: 4 * 3600, rampDownS: 120 };
  }
}

/** A new performance test: 50 users for 30 minutes (3 min ramp-up, 26 steady, 1 down), p95 under 1 s, under 1 % errors. */
export function defaultPerfConfig(scenarios: Scenario[] = []): PerfConfig {
  return {
    version: 2,
    template: 'load',
    load: { mode: 'users', target: PERF_DEFAULT_LOAD.vus, timeUnit: 's', rampUpS: 180, steadyS: 26 * 60, rampDownS: 60 },
    split: 'percent',
    scenarios,
    slos: [
      { metric: 'p95', scope: 'all', max: 1000 },
      { metric: 'errorRate', scope: 'all', max: 0.01 },
    ],
  };
}

/**
 * One scenario of a run as the engine gets it: its script and its own workload. `id` names the k6 scenario
 * ([a-z0-9], or "main" for a single script); `name` goes in front of transaction names when a run has several.
 */
export interface RunScenario {
  id: string;
  name: string;
  script: PerfScript;
  model: WorkloadModel;
  startAfterS?: number;
  thinkTime?: ThinkTime;
  pacingS?: number;
  /** The scenario's data file (its rows come in the job's `rows`, never in the script). */
  data?: RunScenarioData;
}

/** A transaction's name in metrics and goals: "Scenario › Transaction" when the test has several scenarios. */
export const transactionLabel = (scenarioName: string | null, txName: string) => (scenarioName ? `${scenarioName} › ${txName}` : txName);

/** Performance runs go to their own queue, served only by runners with "perf" in RUNNER_PLATFORMS. */
export const PERF_QUEUE_NAME = `${RUN_QUEUE_NAME}-perf`;

/** Run options for a performance test (POST /projects/:id/runs `perf`). */
export const perfRunInputSchema = z
  .object({
    /** Load agents to use; omitted: 1QA picks enough of the project's online, free agents. */
    agentIds: z.array(z.string().min(1).max(40)).min(1).max(20).optional(),
    /** Load shape for this run instead of the test's own (the scenarios keep their shares). */
    load: loadShapeSchema.optional(),
    /** 1 user x 1 iteration with step-by-step results, to check the script before load. */
    verify: z.boolean().optional(),
  })
  .default({});
export type PerfRunInput = z.infer<typeof perfRunInputSchema>;

/** Fixed when a performance run is started (TestRun.perfPlan): later edits of the test or agents don't change it. */
export interface PerfPlan {
  /** The performance test; null for a script check (TestRun.loadScriptId names the script). */
  testCaseId: string | null;
  agentIds: string[];
  /** Each scenario's script (LoadScript id) and its own workload, fixed when the run was started. */
  scenarios: { id: string; name: string; scriptId: string; startAfterS: number; thinkTime: ThinkTime; pacingS?: number; model: WorkloadModel; dataSetId?: string; whenUsedUp?: DataWhenUsedUp }[];
  /** The whole test (scenarios added up): limits, planned duration and the steady window. */
  model: WorkloadModel;
  /** The load shape chosen for this run when it differs from the test's ("Run again" repeats it). */
  load?: LoadShape;
  slos: Slo[];
  verify: boolean;
  /** Users the chosen agents can carry for this script (sum of their estimates); null: no estimate possible. */
  estimateVus: number | null;
  /** Started above the estimate (Project Admins only); the report notes it. */
  aboveEstimate: boolean;
}

/** PASS / FAIL as usual; INVALID: the measurement can't be trusted (e.g. the generator was the bottleneck);
 * DEGRADED: partial results (an agent or samples lost) but still useful. */
export const PERF_VERDICTS = ['PASS', 'FAIL', 'INVALID', 'DEGRADED'] as const;
export type PerfVerdict = (typeof PERF_VERDICTS)[number];

// ---------- Load limits ----------

/** What a new performance test starts with. */
export const PERF_DEFAULT_LOAD = { vus: 50, durationS: 30 * 60 } as const;
/** The most a Project Member may start; above it (up to the project maximum) only Project Admins. */
export const MEMBER_LOAD_LIMIT = { vus: 500, durationS: 30 * 60 } as const;

const hostPattern = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^(\*\.)?[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*(:\d{1,5})?$/, 'a host name like api.example.com or *.example.com');

/** Project Admin settings (TestProject.loadLimits). */
export const loadLimitsSchema = z.object({
  maxVus: z.number().int().min(1).max(100_000),
  maxDurationS: z.number().int().min(60).max(7 * 86_400),
  /** Arrivals per second for open-model tests; null: no extra cap. */
  maxRps: z.number().int().min(1).max(1_000_000).nullable(),
  /** Hosts tests may load besides the project's own app address. */
  allowedHosts: z.array(hostPattern).max(50),
});
export type LoadLimits = z.infer<typeof loadLimitsSchema>;

export const DEFAULT_LOAD_LIMITS: LoadLimits = { maxVus: MEMBER_LOAD_LIMIT.vus, maxDurationS: MEMBER_LOAD_LIMIT.durationS, maxRps: null, allowedHosts: [] };

/** Peak users, peak arrival rate and total duration of a workload. Iteration runs are capped at 10 minutes. */
export function workloadPeak(model: WorkloadModel): { vus: number; rps: number | null; durationS: number } {
  if (model.kind === 'iterations') return { vus: model.vus, rps: null, durationS: 600 };
  const durationS = model.stages.reduce((n, s) => n + s.durationS, 0);
  if (model.kind === 'closed') return { vus: Math.max(...model.stages.map((s) => s.vus)), rps: null, durationS };
  const perS = model.timeUnit === 'm' ? 1 / 60 : 1;
  return { vus: model.maxVus, rps: Math.round(Math.max(...model.stages.map((s) => s.rate)) * perS * 100) / 100, durationS };
}

export type LoadDecision =
  | { ok: true; aboveEstimate: boolean }
  | { ok: false; reason: 'role' | 'member-limit' | 'project-limit' | 'above-estimate'; message: string };

/**
 * May this person start this load? Members: up to 500 users / 30 minutes and within the agents' capacity estimate.
 * Project Admins: up to the project maximum, also above the estimate (the report notes it). Enforced by the API for
 * every start (Run button, schedules and webhooks use the trigger creator's role).
 */
export function checkLoad(orgRole: OrgRole | null | undefined, model: WorkloadModel, limits: LoadLimits, estimateVus: number | null): LoadDecision {
  if (orgRole !== 'PROJECT_ADMIN' && orgRole !== 'MEMBER') return { ok: false, reason: 'role', message: 'Only Project Admins and Members can start load.' };
  const peak = workloadPeak(model);
  const minutes = (s: number) => `${Math.round(s / 60)} minutes`;
  if (peak.vus > limits.maxVus) return { ok: false, reason: 'project-limit', message: `This project allows up to ${limits.maxVus} users; this test needs ${peak.vus}.` };
  if (peak.durationS > limits.maxDurationS) return { ok: false, reason: 'project-limit', message: `This project allows runs of up to ${minutes(limits.maxDurationS)}; this one takes ${minutes(peak.durationS)}.` };
  if (limits.maxRps !== null && peak.rps !== null && peak.rps > limits.maxRps) return { ok: false, reason: 'project-limit', message: `This project allows up to ${limits.maxRps} requests per second; this test asks for ${peak.rps}.` };
  const above = estimateVus !== null && peak.vus > estimateVus;
  if (orgRole === 'MEMBER') {
    if (peak.vus > MEMBER_LOAD_LIMIT.vus || peak.durationS > MEMBER_LOAD_LIMIT.durationS)
      return { ok: false, reason: 'member-limit', message: `Members can start up to ${MEMBER_LOAD_LIMIT.vus} users for ${minutes(MEMBER_LOAD_LIMIT.durationS)}. Ask a Project Admin for more.` };
    if (above) return { ok: false, reason: 'above-estimate', message: `The chosen load agents can carry about ${estimateVus} users of this test. Add an agent, lower the load, or ask a Project Admin.` };
  }
  return { ok: true, aboveEstimate: above };
}

/** Is `url` a host this project's performance tests may load: its own app's host, or an allowed host pattern? */
export function isAllowedLoadTarget(url: string, projectTargetUrl: string | null, allowedHosts: readonly string[]): boolean {
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return false;
  }
  if (!/^(https?|wss?):$/.test(u.protocol)) return false;
  const host = u.hostname.toLowerCase();
  if (projectTargetUrl) {
    try {
      if (new URL(projectTargetUrl).hostname.toLowerCase() === host) return true;
    } catch {
      /* no usable target */
    }
  }
  const port = u.port || (/^(https|wss):$/.test(u.protocol) ? '443' : '80');
  return allowedHosts.some((p) => {
    const [pat = '', patPort] = p.toLowerCase().split(':');
    if (patPort && patPort !== port) return false;
    return pat.startsWith('*.') ? host.endsWith(pat.slice(1)) : host === pat;
  });
}

/** Always blocked for load, whatever the project allows: cloud metadata endpoints. */
export const LOAD_BLOCKED_IPS = ['169.254.0.0/16', '100.100.100.200/32', 'fd00:ec2::254/128'] as const;

// ---------- 1QA Load Agent protocol ----------

/** Load Agent tokens: shown once, stored as a SHA-256 hash. */
export const LOAD_AGENT_TOKEN_PREFIX = '1qa_la_';

export const loadAgentHelloSchema = z.object({
  hostname: z.string().max(100),
  agentVersion: z.string().max(40),
  engine: z.object({ name: z.literal('k6'), version: z.string().max(40) }),
  machine: z.object({
    os: z.string().max(40),
    arch: z.string().max(20),
    cores: z.number().int().min(1).max(1024),
    memMb: z.number().int().min(1),
    freeMemMb: z.number().int().min(0),
    /** min(open-file limit, local ports per target address). */
    socketLimit: z.number().int().min(1),
  }),
  /** Last capacity probe on this machine; null until it has run. */
  probe: z.object({ rpsPerCore: z.number().positive(), memPerVuMb: z.number().positive(), at: z.string().max(40) }).nullable(),
  /** Run the agent is busy with, if any. */
  busyRunId: z.string().max(40).nullable(),
  /** 1.4.0+: whether it was started with a files folder (--files-dir), so scripts can send files from it. */
  filesFolder: z.boolean().optional(),
});
export type LoadAgentHello = z.infer<typeof loadAgentHelloSchema>;

/** Work for a Load Agent (from the runner, relayed by the API). */
export type LoadAgentJob = { id: string; /** Epoch ms after which the job is stale. */ expiresAt: number } & (
  | {
      kind: 'perf-run';
      runId: string;
      resultId: string;
      /** The test's scenarios, each a script with its own workload (k6 scenarios of one run). */
      scenarios: RunScenario[];
      slos: Slo[];
      /** This agent's share when several agents run one test: k6 execution segment `index` of `count`. */
      segment: { index: number; count: number } | null;
      /** Start barrier: every agent of the run starts at this epoch ms. */
      startAt: number;
      /** 1 VU x 1 iteration with step-by-step results (the verify run). */
      verify: boolean;
      /** Test data and secrets of the run's collection, for this run only. Secrets go to the engine's secret source. */
      data: Record<string, string>;
      secrets: Record<string, string>;
      /** Rows of the scenarios' data files, by scenario id (the agent hands them to the engine in a private file). */
      rows?: Record<string, string[][]>;
      /** Test files the scripts send (multipart file fields, file bodies); the agent downloads them before the start. */
      files?: RunFile[];
      /** Hosts the run may reach (everything else, and LOAD_BLOCKED_IPS, is blocked by the agent). */
      allowedHosts: string[];
      userAgent: string;
      insecureSkipTlsVerify: boolean;
    }
  | { kind: 'abort'; runId: string }
  | { kind: 'probe' }
);

/** POST /load-agent/v1/runs/:runId/progress: metrics every few seconds while a run is going (renews the lease). */
export const loadAgentProgressSchema = z.object({
  seq: z.number().int().min(0),
  /** Aggregated 5 s buckets (@1qa/perf BucketJson), new since the last progress. */
  buckets: z.array(z.unknown()).max(500),
  health: z.object({ cores: z.number().min(0), rssMb: z.number().min(0), aggregatorLagMs: z.number().min(0), badLines: z.number().int().min(0) }),
});
export type LoadAgentProgress = z.infer<typeof loadAgentProgressSchema>;

/** Answer to a progress report: keep going, or stop now (Stop button, lease lost, run cancelled). */
export type LoadAgentProgressReply = { action: 'continue' | 'abort' };

export const verifyLineSchema = z.object({
  tx: z.string().max(200),
  step: z.string().max(40),
  name: z.string().max(200),
  status: z.number().int().optional(),
  ok: z.boolean(),
  failedChecks: z.array(z.string().max(300)).max(20).optional(),
  missing: z.array(z.string().max(64)).max(50).optional(),
  why: z.string().max(500).optional(),
  error: z.string().max(500).optional(),
  body: z.string().max(400).optional(),
  /**
   * Check runs only: the request as sent and the response as received, for debugging (secret test data masked, in
   * every encoding, by the load agent's runtime; long values cut). Older agents send none.
   */
  request: z
    .object({ method: z.string().max(10), url: z.string().max(2100), headers: z.array(z.object({ name: z.string().max(210), value: z.string().max(610) })).max(60), body: z.string().max(2100).optional() })
    .optional(),
  response: z
    .object({ status: z.number().int().optional(), timeMs: z.number().int().optional(), headers: z.array(z.object({ name: z.string().max(210), value: z.string().max(610) })).max(60), body: z.string().max(4100).optional() })
    .optional(),
  /** Values this request's response gave (correlation): found (with the value) or not. */
  taken: z.array(z.object({ var: z.string().max(64), found: z.boolean(), value: z.string().max(210).optional() })).max(50).optional(),
});
export type VerifyLine = z.infer<typeof verifyLineSchema>;

/** TestRun.summary of a script check (one user runs a library script once, from the script page). */
export interface ScriptCheckSummary {
  verdict: PerfVerdict;
  reason: string | null;
  /** Each request in order: answered as recorded or not, and why. */
  verify: VerifyLine[];
  requests: number;
  durationMs: number;
}

/** A script check as the script page lists it (GET /perf/scripts/:id/checks). */
export interface ScriptCheckView {
  id: string;
  status: 'QUEUED' | 'RUNNING' | 'PASSED' | 'FAILED' | 'ERROR' | 'CANCELLED';
  queuedAt: string;
  finishedAt: string | null;
  collectionName: string | null;
  triggeredBy: { id: string; name: string };
  /** Why it could not run (no free agent, test data missing…). */
  error: string | null;
  summary: ScriptCheckSummary | null;
}

/** POST /load-agent/v1/runs/:runId/result: once, when the engine has finished. */
export const loadAgentResultSchema = z.object({
  exitCode: z.number().int(),
  /** Engine end-of-test summary (k6 summary export), for totals and thresholds. */
  summary: z.unknown().nullable(),
  verify: z.array(verifyLineSchema).max(1000),
  error: z.string().max(4000).optional(),
  /** Things the run should say, from the engine (a data file used up or started again), each once. */
  notes: z.array(z.string().max(500)).max(20).optional(),
  resources: z.object({ wallSeconds: z.number().min(0), cpuSeconds: z.number().min(0), maxCores: z.number().min(0), maxRssMb: z.number().min(0) }),
});
export type LoadAgentResult = z.infer<typeof loadAgentResultSchema>;

export const LOAD_AGENT_HELLO_EVERY_MS = 10_000;
export const LOAD_AGENT_HEARTBEAT_TTL_S = 30;
export const LOAD_AGENT_POLL_WAIT_S = 25;
/** Progress every 5 s; a run whose agent has not reported for this long has lost its lease and stops by itself. */
export const LOAD_AGENT_PROGRESS_EVERY_MS = 5000;
export const LOAD_RUN_LEASE_MS = 2 * 60_000;

/** Redis keys shared by the API (agent side) and the runner (orchestrator side). */
export const loadAgentKeys = {
  heartbeat: (agentId: string) => `1qa:la:hb:${agentId}`,
  inbox: (agentId: string) => `1qa:la:inbox:${agentId}`,
  /** Held while the agent runs a test (one run per agent). */
  lock: (agentId: string) => `1qa:la:lock:${agentId}`,
  /** Set by the Stop button / cancel; agents get `abort` on their next progress. */
  abort: (runId: string) => `1qa:la:abort:${runId}`,
  /** Progress reports of a run (the API RPUSHes, the runner reads). */
  progress: (runId: string) => `1qa:la:progress:${runId}`,
  /** Final result of one agent's share of a run. */
  result: (runId: string, agentId: string) => `1qa:la:result:${runId}:${agentId}`,
  /** Agents the runner gave a share of the run (a set); only they may report progress and results for it. */
  assigned: (runId: string) => `1qa:la:assigned:${runId}`,
};

export interface LoadAgentView {
  id: string;
  name: string;
  tokenPrefix: string;
  createdBy: { id: string; name: string };
  createdAt: string;
  lastSeenAt: string | null;
  online: boolean;
  paused: boolean;
  busyRunId: string | null;
  hostname: string | null;
  agentVersion: string | null;
  engineVersion: string | null;
  machine: LoadAgentHello['machine'] | null;
  probe: LoadAgentHello['probe'];
  /** Started with a files folder (--files-dir); null while offline or for agents older than 1.4.0. */
  filesFolder: boolean | null;
  /** Entered by whoever connects the agent (a loopback probe can't measure it). */
  bandwidthMbit: number;
  /** Projects that may use it; empty: every project of the organization. */
  projectIds: string[];
  canManage: boolean;
}

export const loadAgentInputSchema = z.object({
  name: z.string().trim().min(1).max(60),
  bandwidthMbit: z.number().int().min(1).max(100_000).default(1000),
  projectIds: z.array(z.string().max(40)).max(200).default([]),
});
export const loadAgentUpdateSchema = z.object({
  name: z.string().trim().min(1).max(60).optional(),
  paused: z.boolean().optional(),
  bandwidthMbit: z.number().int().min(1).max(100_000).optional(),
  projectIds: z.array(z.string().max(40)).max(200).optional(),
});

// ---- Recordings for performance scripts (the 1QA Recorder's performance mode)

/**
 * A recording uploaded by the 1QA Recorder (PerfCapture): the correlation engine has turned its traffic into a script
 * for review. Nothing raw is stored: only the review. Uploads are READY at once (QUEUED / RUNNING / FAILED were used by
 * captures the runner made from functional tests, removed).
 */
export const PERF_CAPTURE_STATUSES = ['QUEUED', 'RUNNING', 'READY', 'FAILED'] as const;
export type PerfCaptureStatus = (typeof PERF_CAPTURE_STATUSES)[number];

export const PERF_CAPTURE_SOURCES = ['runner', 'recorder'] as const;
export type PerfCaptureSource = (typeof PERF_CAPTURE_SOURCES)[number];

/** Limits of a recording uploaded by the 1QA Recorder (MAX_EXCHANGES in @1qa/perf; bodies are capped there too). */
const recordedHeader = z.object({ name: z.string().max(500), value: z.string().max(16_384) });
export const recordedExchangeSchema = z.object({
  id: z.string().max(40),
  action: z.string().max(200).optional(),
  startedMs: z.number(),
  request: z.object({ method: z.string().max(20), url: z.string().max(16_384), headers: z.array(recordedHeader).max(200), body: z.string().max(4_000_000).optional(), mimeType: z.string().max(200).optional() }),
  response: z.object({ status: z.number().int(), headers: z.array(recordedHeader).max(200), body: z.string().max(4_000_000).optional(), mimeType: z.string().max(200).optional() }),
  ws: z.array(z.object({ dir: z.enum(['send', 'receive']), data: z.string().max(1_000_000), timeMs: z.number() })).max(5000).optional(),
});

/** POST /projects/:id/perf/captures/recorded: the Recorder's traffic of one journey, named after the recorded steps. */
export const perfRecordedCaptureSchema = z.object({
  /** The functional test saved from the same recording, if the person saved one (optional: a script needs no test). */
  testCaseId: z.string().min(1).optional(),
  /** Name of the journey (the script's suggested name) when no functional test was saved. */
  name: z.string().trim().min(1).max(200).optional(),
  recording: z.object({ entries: z.array(recordedExchangeSchema).min(1).max(5000) }),
  origins: z.array(z.string().url().max(500)).max(10).default([]),
  keepStatic: z.boolean().default(false),
  /** Backend requests only: leave out pages and front-end framework requests (the recorder's default for performance). */
  backendOnly: z.boolean().default(false),
});
export type PerfRecordedCaptureInput = z.infer<typeof perfRecordedCaptureSchema>;

/** What 1QA found in a recording, for the review screen (HAR import and capture). Session values only as previews. */
export interface PerfReview {
  script: PerfScript;
  correlations: {
    var: string;
    kind: 'extract' | 'generate';
    origin: string;
    source: { stepId: string; where: string } | null;
    generator: unknown;
    usedIn: number;
    confirmed: boolean;
    preview: string;
  }[];
  parameters: { key: string; secret: boolean; usedIn: number }[];
  unresolved: { stepId: string; slot: string; value: string; reason: string }[];
  droppedEntries: number;
  /** Requests to other sites left out of the script (not images or scripts), per host. Older reviews: absent. */
  leftOut?: { host: string; requests: number; methods: string[] }[];
  /** Front-end requests (pages, framework payloads, prefetches) left out of a backend-only script. */
  frontEndLeftOut?: number;
  /** Browser / app repeats left out: CORS preflights, failed requests the app sent again, the same GET twice at once. */
  repeatsLeftOut?: { preflight: number; retried: number; repeated: number };
  /** Requests the app answered with an error while recorded: the script would expect that error again. */
  recordedErrors: { stepId: string; name: string; status: number }[];
  /** Recordings the rules come from: 2 confirms every rule. */
  recordings: 1 | 2;
  /** Why only one recording was used, when two were asked for. */
  note?: string;
}

export interface PerfCaptureView {
  id: string;
  /** The performance project; `sourceProjectId`: the web project of the functional test. */
  projectId: string;
  sourceProjectId: string | null;
  /** The functional test it was made from; null for a recording saved as a script only. */
  testCaseId: string | null;
  /** The test's name, or the recording's name. */
  testName: string;
  collectionName: string | null;
  source: PerfCaptureSource;
  status: PerfCaptureStatus;
  /** What the runner is doing ("Running the test (1 of 2)"). */
  message: string | null;
  error: string | null;
  /** READY: the review, with the script's problems against the project's limits. */
  /**
   * `missingData`: test data the script uses that the performance project does not have (made in another project).
   * `missingFiles`: test files its upload fields name that the project has not uploaded yet ("(not chosen)": a field
   * without a file).
   */
  review: (PerfReview & { problems: string[]; appUrl: string; missingData: string[]; missingFiles?: string[] }) | null;
  createdAt: string;
  finishedAt: string | null;
}

/** "Left out: 4 CORS preflights, 1 request the app sent again after it failed…" (null: nothing left out). */
export function describeRepeats(r: PerfReview['repeatsLeftOut']): string | null {
  if (!r) return null;
  const n = (k: number, one: string, many: string) => (k ? `${k} ${k === 1 ? one : many}` : '');
  const parts = [
    n(r.preflight, 'CORS preflight (OPTIONS, sent by the browser itself)', 'CORS preflights (OPTIONS, sent by the browser itself)'),
    n(r.retried, 'request that failed while recording and that the app sent again (for example after refreshing its sign-in)', 'requests that failed while recording and that the app sent again (for example after refreshing its sign-in)'),
    n(r.repeated, 'request the page sent twice at once', 'requests the page sent twice at once'),
  ].filter(Boolean);
  return parts.length ? `Left out so the script does not fail on them: ${parts.join('; ')}.` : null;
}
